<script setup>
// Media-object cards (Figma node 75-3694). Horizontal whole-card links: a fixed
// 160px image (or icon) panel beside a heading + body + decorative circular
// arrow. Light and dark variants. Stacks to a column below $bp-md.
defineProps({
  block: { type: Object, required: true },
  // { heading?, cards: [{ title, body, to, colour, image?{src,alt}, icon?{src,alt} }] }
})

const isExternal = (to) => typeof to === 'string' && /^https?:\/\//.test(to)
</script>

<template>
  <div class="media-cards">
    <h2 v-if="block.heading" class="block-heading">{{ block.heading }}</h2>

    <ul class="cards">
      <li v-for="(card, i) in block.cards" :key="i">
        <component
          :is="isExternal(card.to) ? 'a' : 'RouterLink'"
          v-bind="isExternal(card.to)
            ? { href: card.to, target: '_blank', rel: 'noopener noreferrer' }
            : { to: card.to }"
          class="card"
          :class="card.colour || 'light'"
        >
          <div class="media" :class="{ icon: card.icon }">
            <img
              v-if="card.image || card.icon"
              :src="(card.image || card.icon).src"
              :alt="(card.image || card.icon).alt"
              loading="lazy"
            />
          </div>
          <div class="text">
            <div class="header-row">
              <h3 class="title">{{ card.title }}</h3>
              <span class="arrow-btn" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M4 10h11m-4-5 5 5-5 5" stroke="currentColor" stroke-width="2"
                    stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </span>
            </div>
            <p v-if="card.body" class="body">{{ card.body }}</p>
          </div>
        </component>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.media-cards {
  .block-heading {
    margin: 0 0 $space-5;
    font-size: $font-size-h2;
    line-height: $lh-heading;
    letter-spacing: $ls-heading;
    font-weight: 500;
    color: var(--color-ink);
  }

  .cards {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: $space-5;
  }

  .card {
    display: flex;
    flex-direction: column;
    gap: $space-5;
    padding: $space-6;
    border: 1px solid var(--color-border-decorative);
    @include rounded($radius-lg);
    text-decoration: none;
    @include transition(opacity, transform);

    @include from-md {
      flex-direction: row;
      align-items: flex-start;
      gap: $space-6;
    }

    &:hover            { opacity: .9; }
    &:hover .arrow-btn { transform: translateX(2px); }
    &:focus-visible    { @include focus-ring; outline: none; }
    &:active           { transform: translateY(1px); }

    &.light {
      background: var(--color-surface-warm);
      color: var(--color-ink);
      .arrow-btn { background: var(--color-ink); color: var(--color-card-cream); }
    }
    &.dark {
      // Always-dark card fill, independent of theme — see _tokens.scss's
      // --color-surface-inverse comment (jk-9-dark-mode.md fix wave; this
      // used to read `var(--color-ink)`, which flips to cream in dark mode,
      // producing cream-on-cream — Known Issues #8).
      background: var(--color-surface-inverse);
      color: var(--color-on-surface-inverse);
      // NOT --color-surface-inverse here — --color-card-cream (the chip's
      // background) is itself theme-overridden to a near-black placeholder
      // in dark mode (_tokens.scss), so the chip is already dark; --color-ink
      // (cream in dark mode) is the correct glyph color against it, same as
      // the .light variant's chip below. This was reverted after an initial
      // (incorrect) swap to --color-surface-inverse produced near-black-on-
      // near-black.
      .arrow-btn { background: var(--color-card-cream); color: var(--color-ink); }
    }

    .media {
      flex-shrink: 0;
      width: 160px;
      height: 160px;
      @include rounded($radius);
      overflow: hidden;

      img { display: block; width: 100%; height: 100%; object-fit: cover; }

      // Icon variant — contained glyph on a subtle fill, not a bleed image.
      &.icon {
        display: flex;
        align-items: center;
        justify-content: center;
        background: var(--color-fill-subtle);
        img { width: 96px; height: 96px; object-fit: contain; }
      }
    }

    .text {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: $space-5;
    }

    .header-row {
      display: flex;
      align-items: center;
      gap: $space-4;
    }

    .title {
      flex: 1;
      min-width: 0;
      margin: 0;
      font-size: $font-size-h4;
      line-height: $lh-heading-sm;
      letter-spacing: $ls-heading;
      font-weight: 500;
    }

    .arrow-btn {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: $radius-pill;
      @include transition(transform);

      svg { width: 20px; height: 20px; display: block; }
    }

    .body {
      margin: 0;
      font-size: $font-size-lg;
      line-height: $lh-base;
    }
  }
}
</style>
